import React, { useState, useEffect } from 'react';
import {
  BarChart3,
  TrendingUp,
  Clock,
  AlertCircle,
  DollarSign,
  Building,
  MapPin,
  FileText,
  RefreshCw,
  Download,
  Calendar,
  Activity
} from 'lucide-react';
import { 
  DashboardData, 
  QuickStats, 
  WorkStage,
  DistrictSummary,
  StageSummary 
} from '../../types/work.types';
import { worksApiService } from '../../services/workApi.service';

// ==========================================
// STAT CARD COMPONENT
// ==========================================

interface StatCardProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  value: string | number;
  subtitle?: string;
  trend?: number;
  color?: 'blue' | 'green' | 'orange' | 'red' | 'purple';
  loading?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ 
  icon: Icon, 
  title, 
  value, 
  subtitle, 
  trend, 
  color = 'blue',
  loading = false 
}) => {
  const colorClasses = {
    blue: 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 text-blue-900',
    green: 'bg-gradient-to-br from-green-50 to-green-100 border-green-200 text-green-900',
    orange: 'bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200 text-orange-900',
    red: 'bg-gradient-to-br from-red-50 to-red-100 border-red-200 text-red-900',
    purple: 'bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 text-purple-900'
  };

  if (loading) {
    return (
      <div className="p-6 rounded-xl border-2 bg-gray-50 border-gray-200 animate-pulse">
        <div className="flex items-center justify-between mb-4">
          <div className="w-8 h-8 bg-gray-300 rounded"></div>
          <div className="w-12 h-4 bg-gray-300 rounded"></div>
        </div>
        <div className="space-y-2">
          <div className="w-24 h-8 bg-gray-300 rounded"></div>
          <div className="w-32 h-4 bg-gray-300 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`p-6 rounded-xl border-2 ${colorClasses[color]} shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1`}>
      <div className="flex items-center justify-between mb-4">
        <Icon className="w-8 h-8 opacity-80" />
        {trend !== undefined && (
          <div className={`flex items-center text-sm font-medium ${trend > 0 ? 'text-green-600' : trend < 0 ? 'text-red-600' : 'text-gray-600'}`}>
            <TrendingUp className={`w-4 h-4 mr-1 ${trend < 0 ? 'rotate-180' : ''}`} />
            {Math.abs(trend).toFixed(1)}%
          </div>
        )}
      </div>
      <div className="space-y-1">
        <p className="text-2xl font-bold">{typeof value === 'number' ? value.toLocaleString('en-IN') : value}</p>
        <p className="text-sm font-medium opacity-80">{title}</p>
        {subtitle && <p className="text-xs opacity-60">{subtitle}</p>}
      </div>
    </div>
  );
};

// ==========================================
// CHART COMPONENTS
// ==========================================

const ProgressChart: React.FC<{ data: StageSummary[]; loading?: boolean }> = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="h-64 bg-gray-50 rounded-lg animate-pulse flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
      </div>
    );
  }

  const maxCount = Math.max(...data.map(d => d.work_count));

  return (
    <div className="space-y-4">
      {data.map((stage, index) => (
        <div key={stage.abstract_stage_of_work} className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-700">{stage.abstract_stage_of_work}</span>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">{stage.work_count} works</span>
              <span className="text-xs text-gray-500">
                {worksApiService.formatCurrency(stage.total_estimate_cost)}
              </span>
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className={`h-3 rounded-full bg-gradient-to-r ${
                index === 0 ? 'from-blue-400 to-blue-600' :
                index === 1 ? 'from-orange-400 to-orange-600' :
                index === 2 ? 'from-green-400 to-green-600' :
                'from-gray-400 to-gray-600'
              }`}
              style={{ width: `${(stage.work_count / maxCount) * 100}%` }}
            ></div>
          </div>
        </div>
      ))}
    </div>
  );
};

const DistrictComparison: React.FC<{ data: DistrictSummary[]; loading?: boolean }> = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="h-64 bg-gray-50 rounded-lg animate-pulse flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
      </div>
    );
  }

  const top5Districts = data
    .sort((a, b) => b.total_expenditure - a.total_expenditure)
    .slice(0, 5);

  return (
    <div className="space-y-4">
      {top5Districts.map((district, index) => (
        <div key={district.district_id} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
            index === 0 ? 'bg-yellow-500' :
            index === 1 ? 'bg-gray-400' :
            index === 2 ? 'bg-orange-500' :
            'bg-blue-500'
          }`}>
            {index + 1}
          </div>
          <div className="flex-1">
            <h4 className="font-medium text-gray-900">{district.district_name}</h4>
            <div className="flex items-center space-x-4 mt-1">
              <span className="text-sm text-gray-600">{district.total_works} works</span>
              <span className="text-sm text-gray-600">
                {Math.round(district.financial_progress_percentage)}% progress
              </span>
            </div>
          </div>
          <div className="text-right">
            <p className="font-semibold text-gray-900">
              {worksApiService.formatCurrency(district.total_expenditure)}
            </p>
            <p className="text-sm text-gray-600">spent</p>
          </div>
        </div>
      ))}
    </div>
  );
};

// ==========================================
// MAIN DASHBOARD COMPONENT
// ==========================================

const Dashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [quickStats, setQuickStats] = useState<QuickStats | null>(null);
  const [districtSummary, setDistrictSummary] = useState<DistrictSummary[]>([]);
  const [stageSummary, setStageSummary] = useState<StageSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // ==========================================
  // DATA FETCHING
  // ==========================================

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [dashboard, stats, districts, stages] = await Promise.all([
        worksApiService.getFinancialDashboard(),
        worksApiService.getQuickStats(),
        worksApiService.getDistrictWiseSummary(),
        worksApiService.getStageWiseSummary()
      ]);

      setDashboardData(dashboard);
      setQuickStats(stats);
      setDistrictSummary(districts);
      setStageSummary(stages);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // ==========================================
  // EVENT HANDLERS
  // ==========================================

  const handleRefresh = () => {
    fetchDashboardData();
  };

  const handleExport = async () => {
    try {
      await worksApiService.downloadExcelExport();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export data');
    }
  };

  // ==========================================
  // RENDER ERROR STATE
  // ==========================================

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-sm border border-red-200 p-8 max-w-md w-full text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Dashboard Error</h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={handleRefresh}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // ==========================================
  // RENDER MAIN DASHBOARD
  // ==========================================

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Works Management Dashboard</h1>
              <p className="text-gray-600 mt-1">
                Overview of construction works and financial progress
                {lastUpdated && (
                  <span className="ml-2 text-sm">
                    • Last updated: {worksApiService.formatDate(lastUpdated, 'long')}
                  </span>
                )}
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleExport}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
              >
                <Download className="w-4 h-4" />
                <span>Export</span>
              </button>
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quick Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={Building}
            title="Total Works"
            value={quickStats?.totalWorks || 0}
            subtitle="Active projects"
            color="blue"
            loading={loading}
          />
          <StatCard
            icon={DollarSign}
            title="Total Sanctioned"
            value={quickStats ? worksApiService.formatCurrency(quickStats.totalSanctioned) : '₹0'}
            subtitle="Approved budget"
            trend={dashboardData?.overview?.utilization_percentage}
            color="green"
            loading={loading}
          />
          <StatCard
            icon={TrendingUp}
            title="Total Spent"
            value={quickStats ? worksApiService.formatCurrency(quickStats.totalSpent) : '₹0'}
            subtitle={`${quickStats?.utilizationPercentage.toFixed(1) || 0}% utilization`}
            color="orange"
            loading={loading}
          />
          <StatCard
            icon={AlertCircle}
            title="Delayed Works"
            value={quickStats?.delayedWorks || 0}
            subtitle="Past deadline"
            color="red"
            loading={loading}
          />
        </div>

        {/* Charts and Analytics Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Work Progress by Stage */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <BarChart3 className="w-5 h-5 mr-2 text-blue-600" />
                Work Progress by Stage
              </h3>
              <div className="text-sm text-gray-500">
                {stageSummary.reduce((sum, s) => sum + s.work_count, 0)} total works
              </div>
            </div>
            <ProgressChart data={stageSummary} loading={loading} />
          </div>

          {/* Top Districts by Expenditure */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <MapPin className="w-5 h-5 mr-2 text-green-600" />
                Top Districts by Expenditure
              </h3>
              <div className="text-sm text-gray-500">
                Top 5 performing districts
              </div>
            </div>
            <DistrictComparison data={districtSummary} loading={loading} />
          </div>
        </div>

        {/* Financial Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center mb-4">
              <DollarSign className="w-6 h-6 text-green-600 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">Financial Health</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Utilization Rate</span>
                <span className="font-semibold text-green-600">
                  {dashboardData?.overview?.utilization_percentage?.toFixed(1) || 0}%
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Avg Work Cost</span>
                <span className="font-semibold">
                  {dashboardData ? worksApiService.formatCurrency(dashboardData.overview.avg_work_cost) : '₹0'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Pending Bills</span>
                <span className="font-semibold text-orange-600">
                  {dashboardData?.overview?.pending_bills_percentage?.toFixed(1) || 0}%
                </span>
              </div>
            </div>
            {dashboardData?.overview && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-green-400 to-green-600 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(dashboardData.overview.utilization_percentage, 100)}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center mb-4">
              <Clock className="w-6 h-6 text-blue-600 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">Project Timeline</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Completed</span>
                <span className="font-semibold text-green-600">
                  {stageSummary.find(s => s.abstract_stage_of_work === WorkStage.COMPLETED)?.work_count || 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">In Progress</span>
                <span className="font-semibold text-blue-600">
                  {stageSummary.find(s => s.abstract_stage_of_work === WorkStage.IN_PROGRESS)?.work_count || 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Not Started</span>
                <span className="font-semibold text-gray-600">
                  {stageSummary.find(s => s.abstract_stage_of_work === WorkStage.NOT_STARTED)?.work_count || 0}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center mb-4">
              <Activity className="w-6 h-6 text-purple-600 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
            </div>
            <div className="space-y-3">
              <div className="flex items-center space-x-3 text-sm">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-gray-600">3 works completed this week</span>
              </div>
              <div className="flex items-center space-x-3 text-sm">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span className="text-gray-600">12 progress updates logged</span>
              </div>
              <div className="flex items-center space-x-3 text-sm">
                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                <span className="text-gray-600">₹2.5Cr bills processed</span>
              </div>
              <div className="flex items-center space-x-3 text-sm">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                <span className="text-gray-600">5 works require attention</span>
              </div>
            </div>
          </div>
        </div>

        {/* Grant Type Analysis */}
        {dashboardData?.grantTypeAnalysis && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-purple-600" />
                Grant Type Analysis
              </h3>
              <div className="text-sm text-gray-500">
                Breakdown by funding source
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {dashboardData.grantTypeAnalysis.map((grant, index) => (
                <div key={`${grant.oas_grant_level}-${grant.grant_type}`} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-gray-900">{grant.grant_type}</h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      grant.oas_grant_level === 'Central' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                    }`}>
                      {grant.oas_grant_level}
                    </span>
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Works</span>
                      <span className="font-medium">{grant.work_count}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Sanctioned</span>
                      <span className="font-medium">{worksApiService.formatCurrency(grant.total_sanctioned)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Spent</span>
                      <span className="font-medium">{worksApiService.formatCurrency(grant.total_spent)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Avg Cost</span>
                      <span className="font-medium">{worksApiService.formatCurrency(grant.avg_cost)}</span>
                    </div>
                  </div>
                  <div className="mt-3">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-500 ${
                          index % 4 === 0 ? 'bg-gradient-to-r from-blue-400 to-blue-600' :
                          index % 4 === 1 ? 'bg-gradient-to-r from-green-400 to-green-600' :
                          index % 4 === 2 ? 'bg-gradient-to-r from-purple-400 to-purple-600' :
                          'bg-gradient-to-r from-orange-400 to-orange-600'
                        }`}
                        style={{ 
                          width: `${grant.total_sanctioned > 0 ? (grant.total_spent / grant.total_sanctioned) * 100 : 0}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Trend Analysis */}
        {dashboardData?.trendAnalysis && dashboardData.trendAnalysis.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Calendar className="w-5 h-5 mr-2 text-indigo-600" />
                Monthly Trends
              </h3>
              <div className="text-sm text-gray-500">
                Last 12 months performance
              </div>
            </div>
            <div className="overflow-x-auto">
              <div className="flex space-x-4 pb-4" style={{ minWidth: '800px' }}>
                {dashboardData.trendAnalysis.map((trend, index) => (
                  <div key={trend.month} className="flex-shrink-0 bg-gray-50 rounded-lg p-4 min-w-[180px]">
                    <div className="text-center mb-3">
                      <h4 className="font-medium text-gray-900">{trend.month}</h4>
                    </div>
                    <div className="space-y-2">
                      <div className="text-center">
                        <div className="text-lg font-bold text-blue-600">{trend.works_sanctioned}</div>
                        <div className="text-xs text-gray-600">works sanctioned</div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm font-semibold text-green-600">
                          {worksApiService.formatCurrency(trend.amount_sanctioned)}
                        </div>
                        <div className="text-xs text-gray-600">sanctioned</div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm font-semibold text-orange-600">
                          {worksApiService.formatCurrency(trend.amount_spent)}
                        </div>
                        <div className="text-xs text-gray-600">spent</div>
                      </div>
                    </div>
                    {/* Progress indicator */}
                    <div className="mt-3">
                      <div className="w-full bg-gray-200 rounded-full h-1">
                        <div
                          className="bg-gradient-to-r from-blue-400 to-green-400 h-1 rounded-full transition-all duration-500"
                          style={{ 
                            width: `${trend.amount_sanctioned > 0 ? (trend.amount_spent / trend.amount_sanctioned) * 100 : 0}%` 
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;